import axios from "../axios"
import type { FeedPost, FeedComment, FeedPortfolio } from "../types/feed.ts"
import { normalizeExploreFeedItem } from "../utils/feedExploreNormalize.ts"
import { unwrapCommentListPayload } from "../utils/unwrapCommentListPayload.ts"
import type { FeedItem } from "../types/feed.ts"

export type FeedExplorePage = {
  items: FeedItem[]
  nextCursor: string | null
  hasMore: boolean
}

function unwrapFeedResponseBody(body: unknown): Record<string, unknown> {
  let cur: unknown = body
  for (let depth = 0; depth < 4; depth++) {
    const dataVal =
      cur != null && typeof cur === "object" && "data" in cur
        ? (cur as { data: unknown }).data
        : undefined
    if (
      dataVal != null &&
      typeof dataVal === "object" &&
      !Array.isArray(dataVal)
    ) {
      cur = dataVal
    } else {
      break
    }
  }
  return cur != null && typeof cur === "object" && !Array.isArray(cur)
    ? (cur as Record<string, unknown>)
    : {}
}

function pickRawExploreItems(source: Record<string, unknown>): unknown[] {
  if (Array.isArray(source.items)) return source.items
  if (Array.isArray(source.data)) return source.data
  if (Array.isArray(source.results)) return source.results
  if (Array.isArray(source.content)) return source.content
  if (Array.isArray(source.response)) return source.response
  const feed = source.feed
  if (feed != null && typeof feed === "object" && !Array.isArray(feed)) {
    const f = feed as Record<string, unknown>
    if (Array.isArray(f.items)) return f.items
  }
  return []
}

function parseFeedExploreBody(body: unknown, pageLimit: number): FeedExplorePage {
  const source = unwrapFeedResponseBody(body)
  const rawItems = pickRawExploreItems(source)
  const items = rawItems.map((raw: unknown) => normalizeExploreFeedItem(raw))

  const pagination =
    source.pagination != null && typeof source.pagination === "object"
      ? (source.pagination as Record<string, unknown>)
      : undefined
  const pageInfo =
    source.pageInfo != null && typeof source.pageInfo === "object"
      ? (source.pageInfo as Record<string, unknown>)
      : undefined
  const meta =
    source.meta != null && typeof source.meta === "object"
      ? (source.meta as Record<string, unknown>)
      : undefined
  const feedBlock =
    source.feed != null && typeof source.feed === "object" && !Array.isArray(source.feed)
      ? (source.feed as Record<string, unknown>)
      : undefined

  const rawCursor =
    source.nextCursor ??
    source.next_cursor ??
    source.cursor ??
    source.after ??
    source.pageToken ??
    source.page_token ??
    feedBlock?.nextCursor ??
    feedBlock?.next_cursor ??
    feedBlock?.cursor ??
    pagination?.cursor ??
    pagination?.next_cursor ??
    pagination?.nextCursor ??
    pagination?.after ??
    pagination?.pageToken ??
    pageInfo?.endCursor ??
    pageInfo?.end_cursor ??
    pageInfo?.nextCursor ??
    pageInfo?.next_cursor ??
    meta?.nextCursor ??
    meta?.next_cursor ??
    meta?.cursor

  let nextCursor =
    rawCursor != null && String(rawCursor) !== "" ? String(rawCursor) : null

  const explicit =
    source.hasMore ??
    source.has_more ??
    feedBlock?.hasMore ??
    feedBlock?.has_more ??
    pagination?.hasMore ??
    pagination?.has_more ??
    pagination?.hasNext ??
    pageInfo?.hasNextPage ??
    pageInfo?.has_next_page ??
    meta?.hasMore ??
    meta?.has_more

  const denyMore =
    explicit === false ||
    (typeof explicit === "number" && explicit === 0) ||
    (typeof explicit === "string" && explicit.trim().toLowerCase() === "false")
  const allowCursorFallback = !denyMore

  if (
    allowCursorFallback &&
    nextCursor == null &&
    items.length === pageLimit &&
    items.length > 0
  ) {
    const last = items[items.length - 1]
    if (last?.id != null) {
      nextCursor = String(last.id)
    }
  }

  let hasMore: boolean
  if (typeof explicit === "boolean") {
    hasMore = explicit
  } else if (typeof explicit === "number") {
    hasMore = explicit !== 0
  } else if (typeof explicit === "string") {
    const low = explicit.trim().toLowerCase()
    hasMore = low === "true" || low === "1"
  } else {
    hasMore = nextCursor != null || items.length === pageLimit
  }

  if (items.length < pageLimit) {
    return { items, nextCursor: null, hasMore: false }
  }

  return { items, nextCursor, hasMore }
}

const getAuthHeaders = (): { headers?: Record<string, string> } => {
  const token = localStorage.getItem("access-Token-Edify")
  if (!token) return {}
  return { headers: { Authorization: `Bearer ${token}` } }
}

export const feedService = {
  getPosteos: async (order: string, type: string): Promise<FeedPost[]> => {
    const { data } = await axios.get(`posteo/list?order=${order}&type=${type}`, getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getFeedExplore: async (params: {
    view: string
    limit?: number
    cursor?: string
  }): Promise<FeedExplorePage> => {
    const search = new URLSearchParams({ view: params.view })
    const limit = params.limit ?? 20
    search.set("limit", String(limit))
    if (params.cursor) search.set("cursor", params.cursor)
    const { data: body } = await axios.get(`feed?${search.toString()}`, getAuthHeaders())
    return parseFeedExploreBody(body, limit)
  },

  getOwnPosteos: async (userId: number | string): Promise<FeedPost[]> => {
    const { data } = await axios.get(`posteo/user?userFind=${userId}`, getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getAllPortfolios: async (): Promise<FeedPortfolio[]> => {
    const { data } = await axios.get("/portfolio/getAll", getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getUserPortfolios: async (userId: number | string): Promise<FeedPortfolio[]> => {
    const { data } = await axios.get(
      `/portfolio/user?userFind=${userId}`,
      getAuthHeaders()
    )
    return Array.isArray(data) ? data : []
  },

  getPostComments: async (postId: number): Promise<FeedComment[]> => {
    const { data } = await axios.get(`/posteo/comments/${postId}`, getAuthHeaders())
    return unwrapCommentListPayload(data)
  },

  getPostById: async (id: number): Promise<FeedPost> => {
    const { data } = await axios.get(`/posteo/${id}`, getAuthHeaders())
    return data
  },

  getPostWithComments: async (
    id: number
  ): Promise<{ post: FeedPost; comments: FeedComment[] }> => {
    const [postRes, commentsRes] = await Promise.all([
      axios.get(`/posteo/${id}`, getAuthHeaders()),
      axios.get(`/posteo/comments/${id}`, getAuthHeaders()),
    ])
    const comments = unwrapCommentListPayload(commentsRes.data)
    return { post: postRes.data, comments }
  },

  likePost: async (postId: number | string): Promise<{ likes?: FeedPost["likes"] }> => {
    const id = Number(postId)
    const { data } = await axios.post<{ likes?: FeedPost["likes"] }>(
      "/posteo/like",
      { post_id: id },
      getAuthHeaders()
    )
    return data ?? {}
  },

  createPost: async (body: {
    content?: string
    video?: string | File
    images?: string | File
  }): Promise<FeedPost> => {
    const formData = new FormData()
    formData.append("content", body.content ?? "")
    formData.append("video", body.video ?? "")
    formData.append("images", body.images ?? "")
    const auth = getAuthHeaders()
    const { data } = await axios.post<FeedPost>("/posteo/create", formData, {
      headers: { ...auth.headers, "content-type": "multipart/form-data" },
    })
    return data
  },

  createPostComment: async (
    postId: number,
    content: string
  ): Promise<void> => {
    await axios.post(
      "/posteo/comment",
      { postId, content },
      getAuthHeaders()
    )
  },

  editPostComment: async (
    commentId: number,
    content: string
  ): Promise<void> => {
    await axios.patch(
      "/posteo/comment/update",
      { commentId, content },
      getAuthHeaders()
    )
  },

  deletePostComment: async (commentId: number): Promise<void> => {
    const auth = getAuthHeaders()
    await axios.delete("/posteo/comment/delete", {
      headers: auth.headers,
      data: { idComment: commentId },
    })
  },

  likePostComment: async (commentId: number): Promise<void> => {
    await axios.post(
      "/posteo/comment/like",
      { post_comment_id: commentId },
      getAuthHeaders()
    )
  },

  responsePostComment: async (
    postId: number,
    commentId: number,
    content: string
  ): Promise<void> => {
    await axios.post(
      "/posteo/comment/response",
      { postId, commentId, content },
      getAuthHeaders()
    )
  },

  deletePost: async (postId: number): Promise<void> => {
    const auth = getAuthHeaders()
    await axios.delete("/posteo/delete", {
      headers: auth.headers,
      data: { idPost: postId },
    })
  },

  updatePost: async (body: {
    id: number
    content?: string
    images?: string | File
    video?: string | File
  }): Promise<FeedPost> => {
    const formData = new FormData()
    formData.append("id", String(body.id))
    formData.append("content", body.content ?? "")
    formData.append("video", body.video ?? "")
    formData.append("images", body.images ?? "")
    const auth = getAuthHeaders()
    const { data } = await axios.patch("/posteo/update", formData, {
      headers: { ...auth.headers, "content-type": "multipart/form-data" },
    })
    return data
  },
}
